import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { TENANT_CONFIG, DEFAULT_TENANT } from '../config/tenant';
import type { TenantId } from '../config/tenant';

interface TenantContextType {
  tenant: TenantId;
  tenantConfig: typeof TENANT_CONFIG[TenantId];
  isValidTenant: boolean;
  switchTenant: (newTenant: TenantId) => void;
}

const TenantContext = createContext<TenantContextType | null>(null);

interface TenantProviderProps {
  children: React.ReactNode;
  tenantOverride?: TenantId;
}

const isTenantId = (value?: string | null): value is TenantId => (
  value === 'cn' || value === 'intl'
);

/**
 * 租户 Provider
 * 从 URL 路径中提取租户信息（/cn/*, /intl/*）
 */
export const TenantProvider: React.FC<TenantProviderProps> = ({ children, tenantOverride }) => {
  const { tenant: tenantParam } = useParams<{ tenant: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const routeTenant = isTenantId(tenantParam) ? tenantParam : undefined;
  const pathTenantCandidate = location.pathname.split('/').filter(Boolean)[0];
  const pathTenant = isTenantId(pathTenantCandidate) ? pathTenantCandidate : undefined;
  const activeTenant = tenantOverride ?? routeTenant ?? pathTenant;

  // 验证并获取租户
  const tenant = useMemo(() => {
    if (activeTenant) {
      return activeTenant;
    }
    // 尝试从 localStorage 获取用户偏好
    const preferred = localStorage.getItem('preferred_tenant') as TenantId | null;
    if (isTenantId(preferred)) {
      return preferred;
    }
    return DEFAULT_TENANT;
  }, [activeTenant]);

  const isValidTenant = Boolean(activeTenant);

  const tenantConfig = TENANT_CONFIG[tenant];

  // 切换租户
  const switchTenant = (newTenant: TenantId) => {
    if (newTenant === tenant) return;
    
    localStorage.setItem('preferred_tenant', newTenant);
    
    // 替换 URL 中的租户前缀
    const currentPath = location.pathname;
    const tenantPrefixPattern = new RegExp(`^/${tenant}(?=/|$)`);
    const newPath = tenantPrefixPattern.test(currentPath)
      ? currentPath.replace(tenantPrefixPattern, `/${newTenant}`)
      : `/${newTenant}${currentPath.startsWith('/') ? currentPath : `/${currentPath}`}`;
    navigate(newPath);
  };

  // 保存用户选择的租户
  useEffect(() => {
    if (isValidTenant) {
      localStorage.setItem('preferred_tenant', tenant);
    }
  }, [tenant, isValidTenant]);

  const value = useMemo(() => ({
    tenant,
    tenantConfig,
    isValidTenant,
    switchTenant,
  }), [tenant, tenantConfig, isValidTenant]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * 使用租户上下文的 Hook
 */
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

/**
 * 可选的租户上下文 Hook（不抛错）
 */
export const useTenantOptional = () => {
  return useContext(TenantContext);
};

export default TenantProvider;
